import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Star,
  UserRound,
  Bell,
  LogOut,
  Settings,
  Search,
} from "lucide-react";
import { logout } from "../../utils/auth";
import { showConfirmDialog } from "../../components/ConfirmDialog";
import { getMoreModuleItems } from "./mobileNavConfig";
import {
  MobilePageShell,
  MobilePageHeader,
  MobileSectionHeader,
  MobileNavGroup,
  MobileNavRow,
  MODULE_TONES,
} from "./MobileNavPrimitives";

const ACCOUNT_LINKS = [
  {
    key: "favorites",
    label: "Thường dùng",
    hint: "Chức năng yêu thích trên máy",
    icon: Star,
    path: "/m/favorites",
    tone: "amber",
  },
  {
    key: "profile",
    label: "Hồ sơ",
    hint: "Thông tin tài khoản",
    icon: UserRound,
    path: "/m/profile",
    tone: "sky",
  },
  {
    key: "notifications",
    label: "Thông báo",
    hint: "Hộp thư hệ thống",
    icon: Bell,
    path: "/system/notifications",
    tone: "emerald",
  },
  {
    key: "settings",
    label: "Cài đặt",
    hint: "Tùy chọn ứng dụng",
    icon: Settings,
    path: "/m/settings",
    tone: "slate",
  },
];

export default function MoreScreen() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const items = useMemo(() => getMoreModuleItems(), []);

  const q = query.trim().toLowerCase();

  const filteredModules = useMemo(() => {
    if (!q) return items;
    return items.filter((item) =>
      String(item.label || "").toLowerCase().includes(q)
    );
  }, [items, q]);

  const filteredAccount = useMemo(() => {
    if (!q) return ACCOUNT_LINKS;
    return ACCOUNT_LINKS.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        (item.hint && item.hint.toLowerCase().includes(q))
    );
  }, [q]);

  const showLogout = !q || "đăng xuất".includes(q) || "dang xuat".includes(q);
  const hasAnyResult =
    filteredModules.length > 0 || filteredAccount.length > 0 || showLogout;

  const handleLogout = async () => {
    const confirm = await showConfirmDialog({
      title: "Xác nhận đăng xuất",
      text: "Bạn có chắc chắn muốn rời khỏi hệ thống không?",
      confirmText: "Đăng xuất",
      cancelText: "Ở lại",
    });
    if (confirm.isConfirmed) {
      logout();
      window.location.href = "/login";
    }
  };

  return (
    <MobilePageShell>
      <MobilePageHeader
        title="Thêm"
        subtitle="Module khác, tài khoản & đăng xuất"
        tone="violet"
      >
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-violet-400">
            <Search size={16} />
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm chức năng..."
            className="w-full rounded-xl border border-violet-100 bg-violet-50/60 py-2.5 pl-9 pr-3 text-sm text-gray-800 outline-none placeholder:text-violet-300 focus:border-violet-200 focus:bg-white focus:ring-2 focus:ring-violet-100"
          />
        </div>
      </MobilePageHeader>

      <div className="space-y-5 px-5 pt-5 pb-4">
        {filteredModules.length > 0 ? (
          <section>
            <MobileSectionHeader title="Module khác" tone="indigo" />
            <MobileNavGroup>
              {filteredModules.map((item) => (
                <MobileNavRow
                  key={`${item.group}-${item.key}`}
                  icon={item.icon}
                  label={item.label}
                  hint={
                    item.path?.startsWith("/m/")
                      ? "Mở trung tâm module"
                      : "Mở module"
                  }
                  tone={item.tone || MODULE_TONES[item.group] || "slate"}
                  onClick={() =>
                    navigate(item.path || `/${item.group}/${item.key}`)
                  }
                />
              ))}
            </MobileNavGroup>
          </section>
        ) : null}

        {filteredAccount.length > 0 ? (
          <section>
            <MobileSectionHeader title="Tài khoản" tone="violet" />
            <MobileNavGroup>
              {filteredAccount.map((item) => (
                <MobileNavRow
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  hint={item.hint}
                  tone={item.tone}
                  onClick={() => navigate(item.path)}
                />
              ))}
            </MobileNavGroup>
          </section>
        ) : null}

        {!hasAnyResult && q ? (
          <p className="py-10 text-center text-[12px] text-gray-400">
            Không tìm thấy “{query.trim()}”
          </p>
        ) : null}

        {showLogout ? (
          <section>
            <MobileNavGroup>
              <MobileNavRow
                icon={LogOut}
                label="Đăng xuất"
                danger
                onClick={handleLogout}
              />
            </MobileNavGroup>
          </section>
        ) : null}
      </div>
    </MobilePageShell>
  );
}
